<template>
  <div>
    <div style="padding-top: 5px; min-height: calc(100vh - 400px) !important" id="page-container">
      <div class="page-section">
        <div :class="containerClass">
          <h2 class="text-center mb-3">
            Video Library
            <i
              class="material-icons icon-16pt clickable-item"
              v-b-popover.hover.top="`Access video library resources to learn new skills.`"
              >info</i
            >
          </h2>
          <page-separator title="" />

          <!-- Loading State -->
          <div v-if="isLoading" class="text-center py-5">
            <b-spinner variant="primary"></b-spinner>
            <p class="text-muted mt-3">Loading video library...</p>
          </div>

          <!-- Empty State -->
          <div
            v-else-if="!libraryItems.length"
            class="d-flex justify-content-center"
          >
            <div class="card card-block card-stretch card-height col-md-8">
              <div class="card-body text-center">
                <i class="material-icons text-muted mt-4 mb-4" style="font-size: 3rem">videocam</i>
                <h4>No Videos Found</h4>
                <p class="text-muted">
                  No video library items are available at the moment. Please check back later.
                </p>
              </div>
            </div>
          </div>

          <!-- Video Grid -->
          <div v-else class="row">
            <div
              class="col-lg-4 col-md-6 mb-4"
              v-for="video in libraryItems"
              :key="video.id"
            >
              <div class="video-card card h-100" @click="playVideo(video)">
                <div class="video-thumbnail-wrapper">
                  <img
                    v-if="video.thumbnail_url && video.thumbnail_url !== 'sdfrj'"
                    :src="video.thumbnail_url"
                    class="video-thumbnail"
                    alt="Video thumbnail"
                  />
                  <div v-else class="video-placeholder">
                    <i class="material-icons">play_circle</i>
                  </div>
                  <div class="play-overlay">
                    <i class="material-icons">play_circle</i>
                  </div>
                </div>
                <div class="card-body">
                  <h5 class="video-title mb-2">{{ video.title }}</h5>
                  <p class="video-description text-muted mb-2">{{ formatDescription(video.description) }}</p>
                  <small class="text-muted">
                    <i class="material-icons icon-16pt">calendar_today</i>
                    {{ formatDate(video.created_at) }}
                  </small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Video Player Modal -->
    <b-modal
      v-model="showVideoModal"
      size="xl"
      title="Video Player"
      hide-footer
      centered
      @shown="onVideoModalShown"
      @hidden="stopVideo"
    >
      <div v-if="selectedVideo" class="video-player-container">
        <h4 class="mb-3">{{ selectedVideo.title }}</h4>

        <div v-if="isEmbedVideo" class="video-embed-wrapper">
          <iframe
            :key="embedVideoUrl"
            :src="embedVideoUrl"
            frameborder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowfullscreen
          ></iframe>
        </div>

        <!-- Direct Video Player -->
        <video
          v-else
          ref="videoPlayer"
          :key="resolvedVideoUrl"
          :src="resolvedVideoUrl"
          class="w-100"
          controls
          controlsList="nodownload"
          @error="handleVideoError"
          style="max-height: 500px; border-radius: 8px; background-color: #000;"
        >
          Your browser does not support the video tag.
        </video>
        <div class="mt-3 text-muted" v-if="selectedVideo.description" v-safe-html="selectedVideo.description" />
        <p class="mt-3 text-muted" v-else>No description available</p>
      </div>
    </b-modal>
  </div>
</template>

<script>
import Page from '@/components/Page.vue';
import PageSeparator from '@/components/Ui/PageSeparator.vue';
import { stripHtmlTags } from '@/common/utils';
import { get } from 'lodash';
import { mapActions, mapGetters } from 'vuex';

export default {
  components: {
    PageSeparator,
  },
  extends: Page,
  name: 'Library',
  data() {
    return {
      title: 'Video Library',
      isLoading: false,
      libraryItems: [],
      assignedCourse: null,
      studentProgram: null,
      showVideoModal: false,
      selectedVideo: null,
    };
  },
  computed: {
    ...mapGetters('auth', ['getLoggedInUser', 'getLoggedInUserDashboard']),
    videoSourceType() {
      const rawUrl = this.get(this.selectedVideo, 'video_url', '');
      return this.getVideoSourceType(rawUrl);
    },
    isEmbedVideo() {
      return this.videoSourceType === 'youtube' || this.videoSourceType === 'drive';
    },
    embedVideoUrl() {
      const rawUrl = this.get(this.selectedVideo, 'video_url', '');
      return this.getEmbedVideoUrl(rawUrl);
    },
    resolvedVideoUrl() {
      const rawUrl = this.get(this.selectedVideo, 'video_url', '');
      return this.getDirectVideoUrl(rawUrl);
    },
    breadcrumb() {
      return [
        { text: this.$t('home'), to: this.routes.home },
        { text: 'Video Library', active: true },
      ];
    },
  },

  methods: {
    ...mapActions('skills', ['getSkillsLibrary', 'createLibraryLog']),
    ...mapActions('courses', ['getStdAssignedCourse']),
    ...mapActions('student', ['getProgramSkills']),
    get,
    stripHtmlTags,

    async fetchStudentData() {
      try {

        // Fetch student program
        const programResp = await this.getProgramSkills();
        this.studentProgram = programResp;
      } catch (error) {
        console.error('Error fetching student data:', error);
      }
    },

    async fetchLibraryItems() {
      this.isLoading = true;
      try {
        // First fetch student course and program data
        await this.fetchStudentData();

        // Prepare params for library API
        const params = {};

        // Add program parameter if available
        if (this.assignedCourse && this.assignedCourse.program) {
          params.program = this.assignedCourse.program;
        } else if (this.studentProgram && this.studentProgram.id) {
          params.program = this.studentProgram.id;
        }

        // Fetch library videos
        const response = await this.getSkillsLibrary(params);
        this.libraryItems = response.data || [];
      } catch (error) {
        console.error('Error fetching library items:', error);
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
        this.libraryItems = [];
      }
      this.isLoading = false;
    },

    async playVideo(video) {
      this.selectedVideo = video;
      this.showVideoModal = true;

      // Log the video view
      try {
        const studentId = this.get(this.getLoggedInUser, 'linked_entity.id') || this.get(this.getLoggedInUser, 'id');
        if (studentId && video.id) {
          await this.createLibraryLog({
            student: studentId,
            skill_library: video.id,
          });
        }
      } catch (error) {
        console.error('Error logging video view:', error);
        // Don't show error to user, just log it
      }

    },

    onVideoModalShown() {
      if (this.isEmbedVideo) return;

      this.$nextTick(async () => {
        const player = this.$refs.videoPlayer;
        if (!player) return;

        player.load();
        try {
          await player.play();
        } catch (error) {
          // keep controls visible for manual play when browser blocks autoplay
        }
      });
    },

    stopVideo() {
      if (this.$refs.videoPlayer) {
        this.$refs.videoPlayer.pause();
        this.$refs.videoPlayer.currentTime = 0;
      }
      this.selectedVideo = null;
    },

    formatDescription(description) {
      const cleanDescription = this.stripHtmlTags(description || '').trim();
      return cleanDescription || 'No description available';
    },

    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },

    getDirectVideoUrl(url) {
      if (!url) return '';

      // Convert Dropbox links to direct download links
      if (url.includes('dropbox.com')) {
        try {
          const parsedUrl = new URL(url);
          parsedUrl.hostname = 'dl.dropboxusercontent.com';
          parsedUrl.searchParams.delete('dl');
          parsedUrl.searchParams.delete('raw');
          parsedUrl.searchParams.set('raw', '1');
          return parsedUrl.toString();
        } catch (error) {
          let directUrl = url.replace('www.dropbox.com', 'dl.dropboxusercontent.com').replace('dl=0', 'raw=1');
          if (!directUrl.includes('raw=1')) {
            directUrl = directUrl.includes('?')
              ? `${directUrl}&raw=1`
              : `${directUrl}?raw=1`;
          }
          return directUrl;
        }
      }

      // For other direct video URLs, return as is
      return url;
    },

    getVideoSourceType(url) {
      if (!url) return 'unknown';

      const normalizedUrl = String(url).trim().toLowerCase();

      if (normalizedUrl.includes('youtube.com') || normalizedUrl.includes('youtu.be')) {
        return 'youtube';
      }

      if (normalizedUrl.includes('drive.google.com')) {
        return 'drive';
      }

      return 'direct';
    },

    getEmbedVideoUrl(url) {
      if (!url) return '';

      const sourceType = this.getVideoSourceType(url);

      if (sourceType === 'youtube') {
        const youtubeId = this.extractYoutubeId(url);
        return youtubeId ? `https://www.youtube.com/embed/${youtubeId}` : '';
      }

      if (sourceType === 'drive') {
        const driveId = this.extractGoogleDriveId(url);
        return driveId ? `https://drive.google.com/file/d/${driveId}/preview` : '';
      }

      return '';
    },

    extractYoutubeId(url) {
      if (!url) return '';

      const normalizedUrl = String(url).trim();
      const shortMatch = normalizedUrl.match(/youtu\.be\/([^?&/]+)/i);
      if (shortMatch && shortMatch[1]) return shortMatch[1];

      const embedMatch = normalizedUrl.match(/youtube\.com\/embed\/([^?&/]+)/i);
      if (embedMatch && embedMatch[1]) return embedMatch[1];

      const watchMatch = normalizedUrl.match(/[?&]v=([^?&/]+)/i);
      if (watchMatch && watchMatch[1]) return watchMatch[1];

      return '';
    },

    extractGoogleDriveId(url) {
      if (!url) return '';

      const normalizedUrl = String(url).trim();
      const fileMatch = normalizedUrl.match(/drive\.google\.com\/file\/d\/([^/]+)/i);
      if (fileMatch && fileMatch[1]) return fileMatch[1];

      const openMatch = normalizedUrl.match(/[?&]id=([^?&]+)/i);
      if (openMatch && openMatch[1]) return openMatch[1];

      return '';
    },

    handleVideoError() {
      this.makeToast({
        variant: 'danger',
        msg: 'Video could not be loaded. Please try again later.',
      });
    },
  },

  async mounted() {
    await this.fetchLibraryItems();
  },

  beforeDestroy() {
    this.stopVideo();
  },
};
</script>

<style lang="scss" scoped>
.video-card {
  cursor: pointer;
  transition: all 0.3s ease;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

    .play-overlay {
      opacity: 1;
    }
  }
}

.video-thumbnail-wrapper {
  position: relative;
  width: 100%;
  height: 200px;
  overflow: hidden;
  background-color: #f5f5f5;
}

.video-thumbnail {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.video-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);

  .material-icons {
    font-size: 4rem;
    color: white;
    opacity: 0.9;
  }
}

.play-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.4);
  opacity: 0;
  transition: opacity 0.3s ease;

  .material-icons {
    font-size: 4rem;
    color: white;
  }
}

.video-title {
  font-size: 1rem;
  font-weight: 600;
  color: #2d3748;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
}

.video-description {
  font-size: 0.875rem;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
}

.video-player-container {
  padding: 1rem;

  h4 {
    color: #2d3748;
    font-weight: 600;
  }
}

.video-embed-wrapper {
  position: relative;
  width: 100%;

  iframe {
    width: 100%;
    height: 500px;
  }
}

.icon-16pt {
  font-size: 16px;
  vertical-align: middle;
  margin-right: 4px;
}

@media (max-width: 768px) {
  .video-thumbnail-wrapper {
    height: 180px;
  }
}
</style>
